/**
 * Data loading for the /admin/pods dashboard.
 *
 * Everything except revenue is live:
 *   - getPodSubCounts()    -> GET admin/pods/counts        (bucket totals)
 *   - getPodOrgCoverage()  -> GET admin/pods/org-coverage  (per-org paid/comped/free)
 *   - getPods()            -> GET admin/pods (meta.total)   (total pods)
 *
 * REVENUE is shown two ways: a list-price estimate (paid pods × flat rate, minus
 * externally-invoiced and Stripe zero-charge pods) and the actual recurring figure
 * from Stripe (admin/pods/counts -> actual_pod_monthly_revenue), which honors
 * coupons, combo pricing, and credit.
 */
import {
    getPodAthleteCoverage,
    getPodOrgCoverage,
    getPodPastDueAthleteCoverage,
    getPods,
    getPodSubCounts,
    PodAthleteCoverageRow,
    PodOrgCoverageRow,
    PodSubCounts,
} from './pods.api';
import {
    CompFreeBreakdown,
    PAID_POD_MONTHLY_RATE,
    PastDueBreakdown,
    PodDashboardData,
    SubscribedBreakdown,
} from './podDashboard.types';

/** Total pods across the system, read from the existing list endpoint's meta. */
const fetchTotalPods = async (): Promise<number | null> => {
    try {
        // per_page=1 keeps the payload tiny; we only need meta.total.
        const res = await getPods({ per_page: 1, page: 1 });
        const total = res.data?.meta?.total;
        return typeof total === 'number' ? total : null;
    } catch (error) {
        console.error('Failed to fetch total pod count', error);
        return null;
    }
};

/** Fetch the per-org coverage rows; empty array on failure. */
const fetchOrgCoverage = async (): Promise<PodOrgCoverageRow[]> => {
    try {
        const res = await getPodOrgCoverage();
        return res.data?.data ?? [];
    } catch (error) {
        console.error('Failed to fetch pod org coverage', error);
        return [];
    }
};

/** Fetch the per-athlete coverage rows; empty array on failure. */
const fetchAthleteCoverage = async (): Promise<PodAthleteCoverageRow[]> => {
    try {
        const res = await getPodAthleteCoverage();
        return res.data?.data ?? [];
    } catch (error) {
        console.error('Failed to fetch pod athlete coverage', error);
        return [];
    }
};

/** Fetch the per-athlete past-due (lapsed-subscription) rows; empty array on failure. */
const fetchPastDueAthleteCoverage = async (): Promise<PodAthleteCoverageRow[]> => {
    try {
        const res = await getPodPastDueAthleteCoverage();
        return res.data?.data ?? [];
    } catch (error) {
        console.error('Failed to fetch pod past-due athlete coverage', error);
        return [];
    }
};

const orgLabel = (row: PodOrgCoverageRow): string =>
    row.organization_name?.trim() || 'Unknown organization';

/** Group per-athlete rows by organization, sorted by pod count descending. */
const groupAthletesByOrg = (
    athleteRows: PodAthleteCoverageRow[],
): SubscribedBreakdown['athlete']['orgs'] => {
    const byOrg = new Map<string, SubscribedBreakdown['athlete']['orgs'][number]>();

    athleteRows.forEach((row) => {
        const holder = row.organization_name?.trim() || 'No organization';
        const key = row.organization_uuid ?? holder;
        let group = byOrg.get(key);
        if (!group) {
            group = { holder, pods: 0, athletes: [] };
            byOrg.set(key, group);
        }
        group.pods += row.pods;
        group.athletes.push({
            individualUuid: row.individual_uuid,
            name: row.athlete_name?.trim() || 'Unknown athlete',
            stripeSubscriptionId: row.stripe_subscription_id,
            pods: row.pods,
        });
    });

    return Array.from(byOrg.values())
        .map((group) => ({
            ...group,
            athletes: group.athletes.sort((a, b) => b.pods - a.pods),
        }))
        .sort((a, b) => b.pods - a.pods);
};

const buildSubscribed = (
    counts: PodSubCounts,
    orgRows: PodOrgCoverageRow[],
    athleteRows: PodAthleteCoverageRow[],
): SubscribedBreakdown => {
    const holders = orgRows
        .filter((r) => r.paid_pods > 0)
        .sort((a, b) => b.paid_pods - a.paid_pods)
        .map((r) => ({
            holder: orgLabel(r),
            stripeCustomerId: r.stripe_customer_id,
            pods: r.paid_pods,
            externalInvoicePods: r.external_invoice_pods,
        }));

    const orgPods = counts.subscribedOrg ?? holders.reduce((sum, h) => sum + h.pods, 0);
    const orgExternalInvoicePods =
        counts.subscribedOrgExternalInvoice ??
        holders.reduce((sum, h) => sum + h.externalInvoicePods, 0);

    const athleteOrgs = groupAthletesByOrg(athleteRows);
    const athletePods = counts.subscribedAthlete ?? athleteOrgs.reduce((sum, o) => sum + o.pods, 0);

    return {
        org: { pods: orgPods, externalInvoicePods: orgExternalInvoicePods, holders },
        athlete: { pods: athletePods, orgs: athleteOrgs },
        zeroChargePods: counts.subscribedZeroChargePods ?? 0,
    };
};

const buildPastDue = (
    counts: PodSubCounts,
    orgRows: PodOrgCoverageRow[],
    athleteRows: PodAthleteCoverageRow[],
): PastDueBreakdown => {
    const orgs = orgRows
        .filter((r) => r.past_due_pods > 0)
        .sort((a, b) => b.past_due_pods - a.past_due_pods)
        .map((r) => ({
            holder: orgLabel(r),
            stripeCustomerId: r.stripe_customer_id,
            pods: r.past_due_pods,
        }));

    const athleteOrgs = groupAthletesByOrg(athleteRows);

    return {
        orgPods: counts.pastDueOrg ?? orgs.reduce((sum, o) => sum + o.pods, 0),
        athletePods: counts.pastDueAthlete ?? athleteOrgs.reduce((sum, o) => sum + o.pods, 0),
        orgs,
        athleteOrgs,
    };
};

const buildCompFree = (counts: PodSubCounts, orgRows: PodOrgCoverageRow[]): CompFreeBreakdown => {
    const orgs = orgRows
        .filter((r) => r.comped_pods > 0 || r.free_pods > 0)
        .sort((a, b) => b.comped_pods + b.free_pods - (a.comped_pods + a.free_pods))
        .map((r) => ({
            holder: orgLabel(r),
            stripeCustomerId: r.stripe_customer_id,
            comped: r.comped_pods,
            free: r.free_pods,
        }));

    return {
        marked: counts.compMarked ?? orgs.reduce((sum, o) => sum + o.comped, 0),
        unmarked: counts.freeUnmarked ?? orgs.reduce((sum, o) => sum + o.free, 0),
        orgs,
    };
};

/** Load the dashboard view-model: live counts + coverage, placeholder revenue. */
export const loadPodDashboardData = async (): Promise<PodDashboardData> => {
    const [counts, orgRows, athleteRows, pastDueAthleteRows, totalPods] = await Promise.all([
        getPodSubCounts(),
        fetchOrgCoverage(),
        fetchAthleteCoverage(),
        fetchPastDueAthleteCoverage(),
        fetchTotalPods(),
    ]);

    const subscribed = buildSubscribed(counts, orgRows, athleteRows);
    const pastDue = buildPastDue(counts, orgRows, pastDueAthleteRows);
    const compFree = buildCompFree(counts, orgRows);

    const subscribedTotal = subscribed.org.pods + subscribed.athlete.pods;
    const pastDueTotal = pastDue.orgPods + pastDue.athletePods;
    const compFreeTotal = compFree.marked + compFree.unmarked;
    const inStock = counts.inStock ?? 0;
    const decommissioned = counts.inStockDecommissioned ?? 0;

    const total =
        totalPods ?? subscribedTotal + pastDueTotal + compFreeTotal + inStock + decommissioned;
    const deployedPercent = total > 0 ? Math.round((subscribedTotal / total) * 100) : 0;

    // List-price estimate excludes externally-invoiced pods (billed off-Stripe at
    // an unknown amount) and Stripe zero-charge pods (100%-off coupon / credit), so
    // the headline estimate doesn't count seats that bill $0.
    const zeroChargePods = subscribed.zeroChargePods;
    const revenuePods = Math.max(
        0,
        subscribed.org.pods -
            subscribed.org.externalInvoicePods +
            subscribed.athlete.pods -
            zeroChargePods,
    );
    const activeMonthlyRevenue = revenuePods * PAID_POD_MONTHLY_RATE;

    // Actual recurring revenue from Stripe (honors coupons + combo pricing). Falls
    // back to the estimate if the backend pull is unavailable.
    const actualMonthlyRevenue = counts.actualPodMonthlyRevenue ?? null;

    return {
        totalPods: total,
        activeMonthlyRevenue,
        actualMonthlyRevenue,
        subscribedZeroChargePods: zeroChargePods,
        deployedPercent,
        segments: [
            { key: 'subscribed', podCount: subscribedTotal, monthlyRevenue: activeMonthlyRevenue },
            { key: 'past_due', podCount: pastDueTotal },
            { key: 'comp_free', podCount: compFreeTotal },
            { key: 'in_stock', podCount: inStock },
            { key: 'decommissioned', podCount: decommissioned },
        ],
        subscribed,
        pastDue,
        compFree,
        placeholders: { revenue: false },
    };
};
